<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { BookOpenCheck, CalendarDays, HeartHandshake, Search, ShieldCheck, SlidersHorizontal, Sparkles } from '@lucide/vue'
import { useRoute } from 'vue-router'
import { api } from '../api'
import SurveyCard from '../components/SurveyCard.vue'
import type { SurveySummary } from '../types'

type CommunityZone = 'heartlight' | 'research'

const surveys = ref<SurveySummary[]>([])
const newSurveys = ref<SurveySummary[]>([])
const categories = ref<Array<{ category: string; count: number }>>([])
const search = ref('')
const activeCategory = ref('全部')
const resultMode = ref<'all' | 'profile' | 'scores_only'>('all')
const activeZone = ref<CommunityZone>('heartlight')
const loading = ref(true)
const route = useRoute()
const creatorNotice = computed(() => route.query.notice === 'creator-invite-required')

const zoneCopy = computed(() => activeZone.value === 'heartlight' ? {
  eyebrow: 'HEARTLIGHT COLLECTION', title: '让轻松的探索，成为一种温柔的相遇',
  description: '娱乐、情绪抒发与日常自我探索在这里相遇。所有公开内容均经过自动初筛与站长人工审核。',
} : {
  eyebrow: 'RESEARCH COLLECTION', title: '让严谨的研究，先被清楚地理解',
  description: '展示经过站长审核的科研与实验问卷，并公开标注 IP 类型、影响等级和材料状态。',
})

async function loadSurveys() {
  loading.value = true
  const params = new URLSearchParams({ zone: activeZone.value })
  if (search.value.trim()) params.set('q', search.value.trim())
  if (activeCategory.value !== '全部') params.set('category', activeCategory.value)
  if (resultMode.value !== 'all') params.set('resultMode', resultMode.value)
  surveys.value = await api(`/api/surveys?${params}`)
  loading.value = false
}

async function loadZone() {
  activeCategory.value = '全部'
  ;[categories.value, newSurveys.value] = await Promise.all([
    api<Array<{ category: string; count: number }>>(`/api/categories?zone=${activeZone.value}`),
    api<SurveySummary[]>(`/api/library/new?zone=${activeZone.value}`),
  ])
  await loadSurveys()
}

let timer: number
watch(search, () => { clearTimeout(timer); timer = window.setTimeout(loadSurveys, 220) })
watch(activeCategory, loadSurveys)
watch(resultMode, loadSurveys)
watch(activeZone, loadZone)
onMounted(loadZone)
</script>

<template>
  <div class="community-page">
    <div v-if="creatorNotice" class="library-access-notice page-wrap"><ShieldCheck/><p><strong>创作工作台采用邀请制</strong><span>你的账号仍可正常填写和保存问卷；如需创作权限，请联系站长加入白名单。</span></p></div>
    <header :class="['community-hero section-pad', activeZone]">
      <div class="community-hero-copy">
        <p class="eyebrow"><Sparkles :size="14"/>心光量域 · {{ zoneCopy.eyebrow }}</p>
        <h1>{{ zoneCopy.title }}</h1>
        <p>{{ zoneCopy.description }}</p>
      </div>
      <nav class="community-zone-switch" aria-label="内容馆分区">
        <button :class="{ active: activeZone === 'heartlight' }" @click="activeZone = 'heartlight'"><HeartHandshake/><span><b>心光馆</b><small>娱乐 · 情绪 · 日常探索</small></span></button>
        <button :class="{ active: activeZone === 'research' }" @click="activeZone = 'research'"><BookOpenCheck/><span><b>量域馆</b><small>科研 · 实验 · 学术量表</small></span></button>
      </nav>
    </header>

    <main class="page-wrap section-pad community-content">
      <section class="hot-board">
        <div class="community-section-heading"><div><p class="eyebrow"><CalendarDays/>NEWLY CURATED</p><h2>今日新入馆</h2></div><p>按通过站长审核的时间展示，不使用浏览行为生成算法热榜。</p></div>
        <div v-if="newSurveys.length" class="hot-topic-strip">
          <SurveyCard v-for="(survey, index) in newSurveys" :key="survey.id" :survey="survey" :index="index" compact/>
        </div>
        <div v-else class="hot-empty"><Sparkles/><span>今天还没有新入馆问卷，可以继续浏览往期精选。</span></div>
      </section>

      <section class="community-feed">
        <div class="community-section-heading feed-heading"><div><p class="eyebrow">TOPIC STREAM</p><h2>{{ activeZone === 'heartlight' ? '发现此刻想回答的话题' : '浏览正在招募的研究' }}</h2></div><b>{{ surveys.length }} 份公开问卷</b></div>
        <div class="explore-toolbar community-toolbar">
          <label class="search-box"><Search :size="19"/><input v-model="search" type="search" :placeholder="activeZone === 'heartlight' ? '搜索情绪、人格、关系或生活主题' : '搜索研究主题、实验方向或量表名称'"/></label>
          <div class="category-scroller" aria-label="问卷分类">
            <button :class="{ active: activeCategory === '全部' }" @click="activeCategory = '全部'">全部</button>
            <button v-for="item in categories" :key="item.category" :class="{ active: activeCategory === item.category }" @click="activeCategory = item.category">{{ item.category }} <small>{{ item.count }}</small></button>
          </div>
          <div class="result-filter" aria-label="结果反馈类型"><button :class="{ active: resultMode === 'all' }" @click="resultMode = 'all'">全部结果</button><button :class="{ active: resultMode === 'profile' }" @click="resultMode = 'profile'">含 IP 角色</button><button :class="{ active: resultMode === 'scores_only' }" @click="resultMode = 'scores_only'">无 IP · 纯维度</button></div>
        </div>

        <div class="results-line"><span><SlidersHorizontal :size="16"/>{{ activeCategory }}</span><b>自动初筛 · 站长人工审核后公开</b></div>
        <div v-if="loading" class="loading-grid"><span v-for="i in 4" :key="i"/></div>
        <div v-else-if="surveys.length" class="community-topic-grid">
          <SurveyCard v-for="(survey, index) in surveys" :key="survey.id" :survey="survey" :index="index"/>
        </div>
        <div v-else class="empty-state"><Search :size="35"/><h2>还没有找到匹配问卷</h2><p>换一个关键词，或浏览其他分类。</p></div>
      </section>
    </main>
  </div>
</template>
